import { useMutation, useQueryClient } from '@tanstack/react-query';

import { policyService } from '../services/policy.service';

import type { ErrorResponse } from '../services/api.types';
import type {
  UpdatePolicyRequest,
  UpdatePolicyResponse,
} from '../services/policy.service';

type MovePolicyParams = {
  comboId: number;
  coverageId: number;
  policyId: number;
  targetComboId: number;
  note: string;
};

type MovePolicyResponse = {
  newCoverageId?: number;
  newComboId?: number;
};

type MovePolicyToNewComboParams = {
  comboId: number;
  coverageId: number;
  policyId: number;
  note: string;
};

type MovePolicyToExistingCoverageParams = {
  comboId: number;
  coverageId: number;
  policyId: number;
  targetCoverageId: number;
  note: string;
};

export const useMovePolicyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MovePolicyResponse, ErrorResponse, MovePolicyParams>({
    mutationFn: async (params) => {
      const response = await policyService.movePolicyToExistingCombo(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.targetComboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['coverage', variables.comboId, variables.coverageId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['coverage', data.newComboId, data.newCoverageId],
          type: 'all',
        }),
      ]);
      queryClient.removeQueries({
        queryKey: ['policy', variables.comboId, variables.coverageId, variables.policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['policy', data.newComboId, data.newCoverageId, variables.policyId],
        refetchType: 'none',
      });
    },
  });
};

export const useMovePolicyToNewComboMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MovePolicyResponse, ErrorResponse, MovePolicyToNewComboParams>({
    mutationFn: async (params) => {
      const response = await policyService.movePolicyToNewCombo(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', data.newComboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['coverage', variables.comboId, variables.coverageId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['coverage', data.newComboId, data.newCoverageId],
          type: 'all',
        }),
      ]);
      queryClient.removeQueries({
        queryKey: ['policy', variables.comboId, variables.coverageId, variables.policyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['policy', data.newComboId, data.newCoverageId, variables.policyId],
        refetchType: 'none',
      });
    },
  });
};

export const useMovePolicyToExistingCoverageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MovePolicyResponse, ErrorResponse, MovePolicyToExistingCoverageParams>({
    mutationFn: async (params) => {
      const response = await policyService.movePolicyToExistingCoverage(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', variables.comboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['comboCoverages', data.newComboId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['coverage', variables.comboId, variables.coverageId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['coverage', data.newComboId, data.newCoverageId],
          type: 'all',
        }),
      ]);

      queryClient.removeQueries({
        queryKey: ['policy', variables.comboId, variables.coverageId, variables.policyId],
      });

      queryClient.invalidateQueries({
        queryKey: ['policy', data.newComboId, data.newCoverageId, variables.policyId],
        refetchType: 'none',
      });
    },
  });
};

// --- Batch mutation types ---

type BatchMovePolicyResponse = {
  newCoverageId?: number;
  newComboId?: number;
  movedCount?: number;
};

type BatchMoveToExistingCoverageParams = {
  comboId: number;
  coverageId: number;
  policyIds: number[];
  targetCoverageId: number;
  note: string;
};

type BatchMoveToExistingComboParams = {
  comboId: number;
  coverageId: number;
  policyIds: number[];
  targetComboId: number;
  note: string;
};

type BatchMoveToNewComboParams = {
  comboId: number;
  coverageId: number;
  policyIds: number[];
  note: string;
};

export const useMovePoliciesBatchToExistingCoverageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<BatchMovePolicyResponse, ErrorResponse, BatchMoveToExistingCoverageParams>({
    mutationFn: async (params) => {
      const response = await policyService.movePoliciesBatchToExistingCoverage(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({ queryKey: ['comboCoverages', variables.comboId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['comboCoverages', data.newComboId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['coverage', variables.comboId, variables.coverageId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['coverage', data.newComboId, data.newCoverageId], type: 'all' }),
      ]);
      variables.policyIds.forEach((pid) => {
        queryClient.removeQueries({ queryKey: ['policy', variables.comboId, variables.coverageId, pid] });
      });
    },
  });
};

export const useMovePoliciesBatchToExistingComboMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<BatchMovePolicyResponse, ErrorResponse, BatchMoveToExistingComboParams>({
    mutationFn: async (params) => {
      const response = await policyService.movePoliciesBatchToExistingCombo(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({ queryKey: ['comboCoverages', variables.comboId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['comboCoverages', data.newComboId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['coverage', variables.comboId, variables.coverageId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['coverage', data.newComboId, data.newCoverageId], type: 'all' }),
      ]);
      variables.policyIds.forEach((pid) => {
        queryClient.removeQueries({ queryKey: ['policy', variables.comboId, variables.coverageId, pid] });
      });
    },
  });
};

type UpdatePolicyParams = {
  comboId: number;
  coverageId: number;
  policyId: number;
  request: UpdatePolicyRequest;
};

export const useUpdatePolicyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdatePolicyResponse, ErrorResponse, UpdatePolicyParams>({
    mutationFn: async ({ comboId, coverageId, policyId, request }) => {
      const response = await policyService.updatePolicy(comboId, coverageId, policyId, request);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({
          queryKey: ['policyPeriodsData', variables.comboId, variables.coverageId, variables.policyId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['policyNamesAndAddressesData', variables.comboId, variables.coverageId, variables.policyId],
          type: 'all',
        }),
        queryClient.refetchQueries({
          queryKey: ['policyUnitStatReport', variables.comboId, variables.coverageId, variables.policyId],
          type: 'all',
        }),
      ]);
    },
  });
};

export const useMovePoliciesBatchToNewComboMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<BatchMovePolicyResponse, ErrorResponse, BatchMoveToNewComboParams>({
    mutationFn: async (params) => {
      const response = await policyService.movePoliciesBatchToNewCombo(params);
      return response.data;
    },
    onSuccess: (data, variables) => {
      void Promise.all([
        queryClient.refetchQueries({ queryKey: ['comboCoverages', variables.comboId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['comboCoverages', data.newComboId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['coverage', variables.comboId, variables.coverageId], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['coverage', data.newComboId, data.newCoverageId], type: 'all' }),
      ]);
      variables.policyIds.forEach((pid) => {
        queryClient.removeQueries({ queryKey: ['policy', variables.comboId, variables.coverageId, pid] });
      });
    },
  });
};
